import { gsap } from 'gsap'

/**
 * ShapeAnimation functionality.
 * @author Dayne Coulson-Hoffacker
 */

export default class ShapeAnimation {
  /**
     * Constructs a new ShapeAnimation instance.
     * @param {Object} options - Options.
     * @param {HTMLElement} options.element - Container.
     */

  constructor(options) {
    this.element = options.element
    this.movableElements = this.element.querySelectorAll('[data-component="moveable-element"]')
    this.speed = 0.35
    this.movementAmount = 500
    this.hoverElementSpeed = 0.15

    this.homeTriangle = this.element.querySelector('#home-triangle')
    this.homeCircleSmall = this.element.querySelector('#home-circle-small')
    this.homeLine = this.element.querySelector('#home-line')
    this.homeCircleLarge = this.element.querySelector('#home-circle-large')
    this.homeHamburger = this.element.querySelector('#home-hamburger')
    this.homeBox = this.element.querySelector('#home-box')

    this.aboutCircleLarge = this.element.querySelector('#about-circle-large')
    this.aboutSemicircleSmall = this.element.querySelector('#about-semicircle-small')
    this.aboutLine = this.element.querySelector('#about-line')
    this.aboutCircleSmall = this.element.querySelector('#about-circle-small')
    this.aboutBox = this.element.querySelector('#about-box')
    this.aboutSemiCircleLarge = this.element.querySelector('#about-semicircle-large')

    this.clientIndustriesCircleOne = this.element.querySelector('#client-industries-circle-one')
    this.clientIndustriesLineThick = this.element.querySelector('#client-industries-line-thick')
    this.clientIndustriesCircleTwo = this.element.querySelector('#client-industries-circle-two')
    this.clientIndustriesCircleSmall = this.element.querySelector('#client-industries-circle-small')
    this.clientIndustriesLineThin = this.element.querySelector('#client-industries-line-thin')

    this.legalServicesLineThick = this.element.querySelector('#legal-services-line-thick')
    this.legalServicesCircleSmall = this.element.querySelector('#legal-services-circle-small')
    this.legalServicesCircleLarge = this.element.querySelector('#legal-services-circle-large')
    this.legalServicesSemiCircle = this.element.querySelector('#legal-services-semicircle')
    this.legalServicesLineThin = this.element.querySelector('#legal-services-line-thin')
    this.legalServicesBox = this.element.querySelector('#legal-services-box')

    this.teamSemiCircle = this.element.querySelector('#team-semicircle')
    this.teamLine = this.element.querySelector('#team-line')
    this.teamTriangle = this.element.querySelector('#team-triangle')
    this.teamCircle = this.element.querySelector('#team-circle')
    this.teamLineThick = this.element.querySelector('#team-line-thick')

    this.defaultLineThin = this.element.querySelector('#default-line-thin')
    this.defaultCircleSmall = this.element.querySelector('#default-circle-small')
    this.defaultSemiCircle = this.element.querySelector('#default-semicircle')
    this.defaultCircleLarge = this.element.querySelector('#default-circle-large')
    this.defaultLineThick = this.element.querySelector('#default-line-thick')

    this.contactTriangle = this.element.querySelector('#contact-triangle')
    this.contactCircleSmall = this.element.querySelector('#contact-circle-small')
    this.contactLine = this.element.querySelector('#contact-line')
    this.contactCircleLarge = this.element.querySelector('#contact-circle-large')
    this.contactLineThick = this.element.querySelector('#contact-line-thick')

    window.addEventListener('mousemove', (e) => {
      this.mouseMoveHandler2(e)
    })

    this.element.addEventListener('mouseenter', (e) => {
      if (this.element.classList.contains('home')) {
        gsap.to(this.homeTriangle, { rotation: -20, xPercent: -25, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.homeCircleSmall, { xPercent: -180, duration: this.hoverElementSpeed })
        gsap.to(this.homeLine, { xPercent: 20, yPercent: -20, duration: this.hoverElementSpeed })
        gsap.to(this.homeCircleLarge, { xPercent: 75, yPercent: -60, duration: this.hoverElementSpeed })
        gsap.to(this.homeHamburger, { rotation: 25, xPercent: 0, yPercent: 50, duration: this.hoverElementSpeed })
        gsap.to(this.homeBox, { rotation: -20, xPercent: -5, yPercent: -30, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('about')) {
        gsap.to(this.aboutCircleLarge, { rotation: 0, xPercent: -10, yPercent: -10, duration: this.hoverElementSpeed })
        gsap.to(this.aboutSemicircleSmall, { rotation: 20, xPercent: 10, yPercent: 5, duration: this.hoverElementSpeed })
        gsap.to(this.aboutLine, { rotation: 15, xPercent: -10, yPercent: 5, duration: this.hoverElementSpeed })
        gsap.to(this.aboutCircleSmall, { rotation: 0, xPercent: -20, yPercent: 10, duration: this.hoverElementSpeed })
        gsap.to(this.aboutBox, { rotation: -20, xPercent: 10, yPercent: 20, duration: this.hoverElementSpeed })
        gsap.to(this.aboutSemiCircleLarge, { rotation: 20, xPercent: 30, yPercent: 20, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('client-industries')) {
        gsap.to(this.clientIndustriesCircleOne, { rotation: 0, xPercent: 10, yPercent: -30, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesLineThick, { rotation: 20, xPercent: -30, yPercent: 10, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesCircleTwo, { rotation: 0, xPercent: -20, yPercent: 20, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesCircleSmall, { rotation: 0, xPercent: 10, yPercent: -50, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesLineThin, { rotation: -10, xPercent: 0, yPercent: -10, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('legal-services')) {
        gsap.to(this.legalServicesLineThick, { rotation: -10, xPercent: -15, yPercent: -10, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesCircleSmall, { rotation: 0, xPercent: 15, yPercent: -60, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesCircleLarge, { rotation: 0, xPercent: 70, yPercent: 30, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesSemiCircle, { rotation: -15, xPercent: 0, yPercent: -30, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesLineThin, { rotation: -15, xPercent: 0, yPercent: 10, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesBox, { rotation: 15, xPercent: 15, yPercent: 15, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('team')) {
        gsap.to(this.teamSemiCircle, { rotation: -20, xPercent: -30, yPercent: -30, duration: this.hoverElementSpeed })
        gsap.to(this.teamLine, { rotation: -15, xPercent: -10, yPercent: 10, duration: this.hoverElementSpeed })
        gsap.to(this.teamTriangle, { rotation: 15, xPercent: 10, yPercent: 15, duration: this.hoverElementSpeed })
        gsap.to(this.teamCircle, { rotation: 0, xPercent: 50, yPercent: -60, duration: this.hoverElementSpeed })
        gsap.to(this.teamLineThick, { rotation: 15, xPercent: -25, yPercent: 60, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('contact')) {
        gsap.to(this.contactTriangle, { rotation: 30, xPercent: 15, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.contactCircleSmall, { rotation: 0, xPercent: -60, yPercent: 140, duration: this.hoverElementSpeed })
        gsap.to(this.contactLine, { rotation: 20, xPercent: 0, yPercent: -10, duration: this.hoverElementSpeed })
        gsap.to(this.contactCircleLarge, { rotation: 0, xPercent: -30, yPercent: -30, duration: this.hoverElementSpeed })
        gsap.to(this.contactLineThick, { rotation: 15, xPercent: 10, yPercent: 5, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('default')) {
        gsap.to(this.defaultLineThin, { rotation: -10, xPercent: 50, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.defaultCircleSmall, { rotation: 0, xPercent: -50, yPercent: -10, duration: this.hoverElementSpeed })
        gsap.to(this.defaultSemiCircle, { rotation: 40, xPercent: -20, yPercent: 10, duration: this.hoverElementSpeed })
        gsap.to(this.defaultCircleLarge, { rotation: 0, xPercent: 25, yPercent: 25, duration: this.hoverElementSpeed })
        gsap.to(this.defaultLineThick, { rotation: -15, xPercent: 0, yPercent: 10, duration: this.hoverElementSpeed })
      } else {
        this.movementAmount = 500
      }
    })

    this.element.addEventListener('mouseleave', (e) => {
      if (this.element.classList.contains('home')) {
        gsap.to(this.homeTriangle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.homeCircleSmall, { xPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.homeLine, { xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.homeCircleLarge, { xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.homeHamburger, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.homeBox, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('about')) {
        gsap.to(this.aboutCircleLarge, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.aboutSemicircleSmall, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.aboutLine, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.aboutCircleSmall, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.aboutBox, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.aboutSemiCircleLarge, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('client-industries')) {
        gsap.to(this.clientIndustriesCircleOne, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesLineThick, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesCircleTwo, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesCircleSmall, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.clientIndustriesLineThin, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('legal-services')) {
        gsap.to(this.legalServicesLineThick, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesCircleSmall, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesCircleLarge, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesSemiCircle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesLineThin, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.legalServicesBox, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('team')) {
        gsap.to(this.teamSemiCircle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.teamLine, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.teamTriangle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.teamCircle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.teamLineThick, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('contact')) {
        gsap.to(this.contactTriangle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.contactCircleSmall, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.contactLine, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.contactCircleLarge, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.contactLineThick, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else if (this.element.classList.contains('default')) {
        gsap.to(this.defaultLineThin, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.defaultCircleSmall, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.defaultSemiCircle, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.defaultCircleLarge, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
        gsap.to(this.defaultLineThick, { rotation: 0, xPercent: 0, yPercent: 0, duration: this.hoverElementSpeed })
      } else {
        this.movementAmount = 500
      }
    })
  }

  mouseMoveHandler2(e) {
    this.movableElements.forEach(
      (movableElement) => {
        const shiftValue = movableElement.getAttribute('data-value');
        const moveX = (e.clientX * shiftValue) / this.movementAmount;
        const moveY = (e.clientY * shiftValue) / this.movementAmount;

        gsap.to(movableElement, { x: moveX, y: moveY, duration: this.speed })

      }
    )
  }
}
